import loadable from '@loadable/component';

const Login = loadable(() => import('common/auth/Login'));
const Reports = loadable(() => import('pages/reports/Reports'));
const Blackouts = loadable(() => import('pages/blackouts/Blackouts'));
const Changelog = loadable(() => import('pages/changelog/Changelog'));
const Dashboard = loadable(() => import('pages/dashboard/Dashboard'));
const Inventory = loadable(() => import('pages/inventory/Inventory'));
const Accounting = loadable(() => import('pages/accounting/Accounting'));
const TransientRates = loadable(
    () => import('pages/rates/transient/TransientRates')
);
const EmployeeParking = loadable(
    () => import('pages/employee-parking/EmployeeParking')
);
const Airport = loadable(() => import('pages/rates/airport/Airport'));
const Monthly = loadable(() => import('pages/rates/monthly/Monthly'));
const Reservations = loadable(
    () => import('pages/reservations/all-reservations/Reservations')
);
const EventManagement = loadable(
    () => import('pages/event-management/EventManagement')
);

const EmailRecipients = loadable(
    () => import('pages/facilities/email-recipients/EmailRecipients')
);
const LocationInfo = loadable(
    () => import('pages/facilities/location-info/LocationInfo')
);
const DriverFeedback = loadable(
    () => import('pages/facilities/driver-feedback/DriverFeedback')
);
const TodaysReservations = loadable(
    () => import('pages/reservations/todays-reservations/TodaysReservations')
);
const EventDayOverview = loadable(
    () => import('pages/event-management/event-day-overview/EventDayOverview')
);
const ComparativeSnapshot = loadable(
    () => import('pages/rates/comparative-snapshot/ComparativeSnapshot')
);
const CompPasses = loadable(() => import('pages/comp-pass/CompPasses'));

/**
 * @typedef {Object} ROUTES
 * @property {string} login - The login route
 * @property {string} reports - The reports route with an optional tabId parameter
 * @property {string} blackouts - The blackouts route
 * @property {string} dashboard - The dashboard route
 * @property {string} changelog - The changelog route
 * @property {string} accounting - The accounting route
 * @property {string} reservations - The reservations route
 * @property {Object} facilities - Object containing facilities related routes
 * @property {string} facilities.facilities - The facilities route
 * @property {string} facilities.facility - The facility route with the facilityId parameter
 * @property {string} facilities.facilityTabs - The facility tabs route with an optional facilityId and tabId parameter
 * @property {string} facilities.locationInfo - The location info route with the facilityId parameter
 * @property {string} facilities.additionalAdmins - The additional admins route with the facilityId parameter
 * @property {string} driverFeedback - The driver feedback route with the facilityId parameter and optional tabId parameter
 * @property {string} todaysReservations - The onsite operations route with the facilityId parameter
 * @property {string} inventory - The inventory route with the facilityId parameter
 * @property {Object} eventManagement - Object containing event management related routes
 * @property {string} eventManagement.eventManagement - The event management route with the facilityId and optional tabId parameter
 * @property {Object} rates - Object containing rates related routes
 * @property {string} rates.rates - The rates route with the facilityId parameter
 * @property {string} rates.airport - The airport rates route with the facilityId parameter
 * @property {string} rates.monthly - The monthly rates route with the facilityId parameter
 * @property {string} rates.standardRates - The standard rates route with the facilityId parameter
 * @property {string} rates.spotheroIQ - The Spothero IQ rates route with the facilityId parameter
 * @property {string} rates.flexRates - The flex rates route with the facilityId parameter
 * @property {string} rates.comparativeSnapshot - The Comparative Snapshot route with the facilityId parameter
 *
 * @memberof module:constants
 */
export const ROUTES = {
    login: '/login',
    reports: '/refunds',
    blackouts: '/blackouts',
    dashboard: '/dashboard',
    changelog: '/changelog',
    accounting: '/accounting',
    reservations: '/reservations',
    todaysReservations: '/facilities/:facilityId/todays-reservations',
    compPasses: '/facilities/:facilityId/comp-passes',
    facilities: {
        facility: '/facilities/:facilityId?',
        locationInfo: '/facilities/:facilityId/location-info',
        emailRecipients: '/facilities/:facilityId/email-recipients',
    },
    driverFeedback: '/facilities/:facilityId/driver-feedback/:tabId',
    starRatingsAndReviews:
        '/facilities/:facilityId/driver-feedback/star-ratings-and-review-tags',
    inventory: '/facilities/:facilityId/inventory',
    eventManagement: '/facilities/:facilityId/event-management/:tabId?',
    eventManagementDayOverview: `/facilities/:facilityId/event-management/details/:date/:tabId?`,
    rates: {
        rates: '/facilities/:facilityId/rates',
        airport: '/facilities/:facilityId/rates/airport/:tabId?',
        monthly: '/facilities/:facilityId/rates/monthly',
        standardRates: '/facilities/:facilityId/rates/standard',
        spotheroIQ: '/facilities/:facilityId/rates/spothero-iq',
        flexRates: '/facilities/:facilityId/rates/flex-rates',
        transient: '/facilities/:facilityId/rates/transient-rates',
        comparativeSnapshot: '/facilities/:facilityId/comparative-snapshot',
    },
    employeeParking: '/employee-parking',
};

/**
 * Object representing redirects in the application.
 *
 * @typedef {Object} Redirects
 * @property {string} root - The root redirect ('/').
 * @property {string} catchAll - The catch-all redirect ('*').
 * @property {string} reports - The reports redirect ('/reports').
 * @property {string} spotheroIQ - The spothero-iq redirect ('/spothero-iq').
 * @property {string} rates - The rates redirect ('/rates').
 * @property {string} microclimate - The microclimate redirect ('/microclimate').
 *
 * @memberof module:constants
 */
export const REDIRECTS = {
    root: '/',
    catchAll: '*',
    reports: '/reports',
    spotheroIQ: '/spothero-iq',
    rates: '/rates',
    microclimate: '/facilities/:facilityId/microclimate',
    additionalAdmins: '/facilities/:facilityId/additional-admins',
};

/**
 * Configuration for the routes in the application.  This is used to generate the routes in the router.  The order of the elements in the Route Config array will affect the order in which a route match takes precedence.
 *
 * @typedef {Object} RouteConfig
 * @property {React.Component} [component] - The component to render for the route.
 * @property {string} [name] - The name of the route.
 * @property {string} [path] - The path of the route.
 * @property {boolean} [exact] - Flag indicating if the route should be an exact match.
 * @property {boolean} [protected] - Flag indicating if the route requires authentication.
 * @property {string} [redirect] - The path to redirect to if the route matches.
 *
 * @memberof module:constants
 */
export const ROUTE_CONFIG = [
    {component: Login, path: ROUTES.login, exact: true, protected: false},
    {
        path: [
            REDIRECTS.spotheroIQ,
            `${REDIRECTS.spotheroIQ}/:tabId`,
            `${ROUTES.rates.spotheroIQ}/:tabId`,
            REDIRECTS.rates,
            ROUTES.rates.flexRates,
            ROUTES.rates.spotheroIQ,
            ROUTES.rates.standardRates,
        ],
        redirect: ROUTES.rates.transient,
    },
    {
        path: REDIRECTS.additionalAdmins,
        redirect: ROUTES.facilities.emailRecipients,
    },
    {
        component: Reports,
        name: 'Reports',
        path: ROUTES.reports,
        exact: true,
        protected: true,
        state: {
            multiFacility: true,
        },
    },
    {
        component: Blackouts,
        name: 'Blackouts',
        path: ROUTES.blackouts,
        exact: true,
        protected: true,
        state: {
            multiFacility: true,
        },
    },
    {
        component: Dashboard,
        name: 'Dashboard',
        path: ROUTES.dashboard,
        exact: true,
        protected: true,
        state: {
            multiFacility: true,
        },
    },
    {
        component: Changelog,
        name: 'Changelog',
        path: ROUTES.changelog,
        exact: true,
        protected: true,
        state: {
            multiFacility: true,
        },
    },
    {
        component: Accounting,
        name: 'Accounting',
        path: ROUTES.accounting,
        exact: true,
        protected: true,
        state: {
            multiFacility: true,
        },
    },
    {
        component: Reservations,
        name: 'Reservations',
        path: ROUTES.reservations,
        exact: true,
        protected: true,
        state: {
            multiFacility: true,
        },
    },
    {
        component: CompPasses,
        name: 'Comp Passes',
        path: ROUTES.compPasses,
        exact: true,
        protected: true,
    },
    {
        component: Inventory,
        name: 'Inventory',
        path: ROUTES.inventory,
        exact: true,
        protected: true,
    },
    {
        component: EventManagement,
        name: 'Event Management',
        path: ROUTES.eventManagement,
        exact: true,
        protected: true,
    },
    {
        component: EventDayOverview,
        name: 'Event Day Overview',
        path: ROUTES.eventManagementDayOverview,
        exact: true,
        protected: true,
    },
    {
        component: Airport,
        name: 'Airport',
        path: ROUTES.rates.airport,
        exact: true,
        protected: true,
    },
    {
        component: Monthly,
        name: 'Monthly',
        path: ROUTES.rates.monthly,
        exact: true,
        protected: true,
    },
    {
        component: TransientRates,
        name: 'Transient Rates',
        path: ROUTES.rates.transient,
        protected: true,
        trackingDisabled: true,
    },
    {
        component: EmployeeParking,
        name: 'Employee Parking',
        path: ROUTES.employeeParking,
        protected: true,
        exact: true,
        state: {
            multiFacility: true,
        },
    },
    {
        path: [
            '/facilities/:facilityId/driver-feedback/driver-feedback', // bad alias
            '/facilities/:facilityId/driver-feedback/review-tags', // old tab
        ],
        redirect: ROUTES.starRatingsAndReviews,
    },
    {
        component: DriverFeedback,
        name: 'Driver Feedback',
        path: ROUTES.driverFeedback,
        exact: true,
        protected: true,
    },
    {
        path: ['/facilities/:facilityId/driver-feedback'],
        redirect: ROUTES.starRatingsAndReviews,
    },
    {
        component: TodaysReservations,
        name: 'Todays Reservations',
        path: ROUTES.todaysReservations,
        exact: true,
        protected: true,
    },
    {
        path: REDIRECTS.microclimate,
        redirect: ROUTES.facilities.locationInfo,
    },
    {
        component: ComparativeSnapshot,
        name: 'Comparative Snapshot',
        path: ROUTES.rates.comparativeSnapshot,
        exact: true,
        protected: true,
    },
    {
        component: LocationInfo,
        name: 'Facility Location Info',
        path: ROUTES.facilities.locationInfo,
        exact: true,
        protected: true,
    },
    {
        component: EmailRecipients,
        name: 'Facility Email Recipients',
        path: ROUTES.facilities.emailRecipients,
        exact: true,
        protected: true,
    },
    // setup redirects
    {
        path: ROUTES.facilities.facility,
        redirect: ROUTES.facilities.locationInfo,
    },
    {
        path: REDIRECTS.reports,
        redirect: ROUTES.reports,
    },
    {
        path: REDIRECTS.root,
        redirect: ROUTES.dashboard,
    },
    {
        path: REDIRECTS.catchAll,
        redirect: ROUTES.dashboard,
    },
];
